import { buildStyles } from './styles'
import { CHAT_ICON, SEND_ICON } from './icons'
import { renderRichText } from './richtext'
import type { ChatMessage, WidgetConfig } from './types'

const EDGE_GAP = 20
const LAUNCHER_SIZE = 56
const PANEL_GAP_ABOVE_LAUNCHER = 12
const MOBILE_BREAKPOINT = '(max-width: 480px)'

export type WidgetUI = {
  addMessage(role: 'user' | 'assistant', text: string): void
  showTyping(): void
  hideTyping(): void
  showError(text: string): void
  clearInput(): void
  open(): void
  close(): void
}

export function mountWidget(
  shadowRoot: ShadowRoot,
  config: WidgetConfig,
  history: ChatMessage[],
  onSend: (text: string) => void
): WidgetUI {
  const style = document.createElement('style')
  style.textContent = buildStyles(config.accentColor)
  shadowRoot.appendChild(style)

  const isLeft = config.position === 'bottom-left'
  const sideProp = isLeft ? 'left' : 'right'
  const basePanelBottom = EDGE_GAP + LAUNCHER_SIZE + PANEL_GAP_ABOVE_LAUNCHER

  const launcher = document.createElement('button')
  launcher.className = 'pw-launcher'
  launcher.setAttribute('aria-label', 'Abrir chat')
  launcher.innerHTML = CHAT_ICON

  const panel = document.createElement('div')
  panel.className = 'pw-panel'

  const header = document.createElement('div')
  header.className = 'pw-header'
  const title = document.createElement('span')
  title.className = 'pw-title'
  title.textContent = config.displayName
  const closeBtn = document.createElement('button')
  closeBtn.className = 'pw-close'
  closeBtn.setAttribute('aria-label', 'Fechar chat')
  closeBtn.textContent = '✕'
  header.append(title, closeBtn)

  const messages = document.createElement('div')
  messages.className = 'pw-messages'

  const inputBar = document.createElement('div')
  inputBar.className = 'pw-input-bar'
  const input = document.createElement('input')
  input.className = 'pw-input'
  input.type = 'text'
  input.placeholder = 'Escreva sua mensagem...'
  const sendBtn = document.createElement('button')
  sendBtn.className = 'pw-send'
  sendBtn.setAttribute('aria-label', 'Enviar')
  sendBtn.innerHTML = SEND_ICON
  inputBar.append(input, sendBtn)

  panel.append(header, messages, inputBar)
  shadowRoot.append(launcher, panel)

  /**
   * Reposiciona launcher/painel considerando dois problemas reais de mobile:
   *
   * 1. No modo tela cheia (@media max-width: 480px em styles.ts), o painel
   *    usa `inset: 0` — mas um inline style (bottom/right/left setado aqui
   *    em JS) sempre vence uma regra de stylesheet, mobile ou não. Setar
   *    bottom/side incondicionalmente (como antes) deixava uma folga real
   *    entre o painel e a borda da tela no mobile, com o site aparecendo
   *    por trás — por isso só aplicamos bottom/side quando NÃO estamos no
   *    breakpoint mobile; no mobile, o CSS cuida sozinho.
   * 2. O teclado do iOS Safari não reflui position:fixed — o input fica
   *    embaixo do teclado, inacessível. window.visualViewport reflete a
   *    área realmente visível (exclui o teclado); a diferença pra
   *    window.innerHeight é ~a altura do teclado. No desktop/floating,
   *    soma isso ao bottom; no mobile tela cheia, encolhe a altura do
   *    painel pra essa área visível (senão o rodapé do painel, onde fica o
   *    input, continua atrás do teclado mesmo com o painel "tela cheia").
   */
  const mobileQuery = window.matchMedia(MOBILE_BREAKPOINT)

  function updateViewportPosition(): void {
    const vv = window.visualViewport
    const keyboardHeight = vv ? Math.max(0, window.innerHeight - vv.height - vv.offsetTop) : 0

    if (mobileQuery.matches) {
      panel.style.bottom = ''
      panel.style.left = ''
      panel.style.right = ''
      panel.style.top = vv ? `${vv.offsetTop}px` : ''
      panel.style.height = vv ? `${vv.height}px` : ''
    } else {
      panel.style.top = ''
      panel.style.height = ''
      panel.style.bottom = `${basePanelBottom + keyboardHeight}px`
      panel.style[sideProp] = `${EDGE_GAP}px`
    }
    launcher.style.bottom = `${EDGE_GAP + keyboardHeight}px`
    launcher.style[sideProp] = `${EDGE_GAP}px`
  }

  updateViewportPosition()
  window.visualViewport?.addEventListener('resize', updateViewportPosition)
  window.visualViewport?.addEventListener('scroll', updateViewportPosition)
  window.addEventListener('resize', updateViewportPosition)

  let typingEl: HTMLDivElement | null = null

  const ui: WidgetUI = {
    addMessage(role, text) {
      const bubble = document.createElement('div')
      bubble.className = `pw-bubble pw-bubble--${role}`
      // Mensagem do usuário é o que ele mesmo digitou — texto puro, sem
      // parse. Resposta do agente vem em Markdown (ver richtext.ts).
      if (role === 'assistant') {
        renderRichText(bubble, text)
      } else {
        bubble.textContent = text
      }
      // Altura ANTES de inserir a bolha nova = onde ela começa. Pra
      // respostas do agente, rola só até esse ponto (o topo da mensagem
      // nova), não até o fim do container: uma resposta longa o bastante
      // pra ocupar a altura toda do painel (comum no mobile, onde o painel
      // é tela cheia) deve abrir mostrando o COMEÇO dela, não o final —
      // senão o usuário precisa rolar pra cima só pra ler do início.
      // Mensagem do usuário continua rolando até o fim (ela é curta, e o
      // indicador de "digitando" aparece logo depois).
      const topOfNewBubble = messages.scrollHeight
      messages.appendChild(bubble)
      messages.scrollTop = role === 'assistant' ? topOfNewBubble : messages.scrollHeight
    },
    showTyping() {
      if (typingEl) return
      typingEl = document.createElement('div')
      typingEl.className = 'pw-bubble pw-bubble--assistant'
      typingEl.innerHTML = '<span class="pw-typing"><span></span><span></span><span></span></span>'
      messages.appendChild(typingEl)
      messages.scrollTop = messages.scrollHeight
    },
    hideTyping() {
      typingEl?.remove()
      typingEl = null
    },
    showError(text) {
      const bubble = document.createElement('div')
      bubble.className = 'pw-bubble pw-bubble--error'
      bubble.textContent = text
      messages.appendChild(bubble)
      messages.scrollTop = messages.scrollHeight
    },
    clearInput() {
      input.value = ''
    },
    open() {
      panel.classList.add('pw-open')
      input.focus()
    },
    close() {
      panel.classList.remove('pw-open')
    },
  }

  let open = false
  const toggle = () => {
    open = !open
    if (open) ui.open()
    else ui.close()
  }
  launcher.addEventListener('click', toggle)
  closeBtn.addEventListener('click', toggle)

  const submit = () => {
    const text = input.value.trim()
    if (!text) return
    onSend(text)
  }
  sendBtn.addEventListener('click', submit)
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') submit()
  })

  // Histórico restaurado (reload, aba nova do mesmo site) substitui a
  // saudação — mostrar as duas seria redundante e confuso.
  if (history.length > 0) {
    for (const message of history) {
      ui.addMessage(message.role, message.text)
    }
  } else if (config.greeting) {
    ui.addMessage('assistant', config.greeting)
  }

  return ui
}
